import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTemplates } from '@/shared/hooks/useCloudData';
import { useLanguage } from '@/shared/contexts/LanguageContext';
import { FileText, ArrowRight, SkipForward, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { WizardState } from '../../hooks/useSessionWizard';

interface Props {
  wizard: {
    state: WizardState;
    skipContract: () => Promise<void>;
  };
}

function templateName(name: unknown, lang: string): string {
  if (typeof name === 'string') return name;
  if (name && typeof name === 'object') {
    const n = name as Record<string, string>;
    return n[lang] ?? n['fr'] ?? Object.values(n)[0] ?? '';
  }
  return '';
}

export default function Step3Contract({ wizard }: Props) {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { data: templates = [] } = useTemplates();
  const [skipping, setSkipping] = useState(false);

  const handleSelectTemplate = (templateId: string) => {
    const params = new URLSearchParams();
    if (wizard.state.clientId) params.set('client_id', wizard.state.clientId);
    if (wizard.state.sessionId) params.set('sessionId', wizard.state.sessionId);
    navigate(`/contract/${templateId}?${params.toString()}`);
  };

  const handleSkip = async () => {
    setSkipping(true);
    try {
      await wizard.skipContract();
    } finally {
      setSkipping(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-bold font-display mb-1">Contrat de consentement</h2>
        <p className="text-sm text-muted-foreground">Sélectionnez un modèle à faire signer au client</p>
      </div>

      {templates.length === 0 ? (
        <div className="glass-card p-8 text-center">
          <FileText className="mx-auto text-muted-foreground mb-2" size={32} />
          <p className="text-sm text-muted-foreground">Aucun modèle disponible</p>
        </div>
      ) : (
        <ul className="space-y-2">
          {templates.map((tpl: any) => (
            <li key={tpl.id}>
              <button
                onClick={() => handleSelectTemplate(tpl.id)}
                className="w-full text-left glass-card p-4 hover:border-primary hover:bg-primary/5 transition-all duration-200 flex items-center justify-between gap-3"
              >
                <div className="flex items-center gap-3">
                  <FileText size={18} className="text-primary shrink-0" />
                  <span className="font-medium text-sm">{templateName(tpl.name, language)}</span>
                </div>
                <ArrowRight size={16} className="text-muted-foreground shrink-0" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <Button
        variant="outline"
        className="w-full gap-2"
        onClick={handleSkip}
        disabled={skipping}
      >
        {skipping ? (
          <Loader2 size={16} className="animate-spin" />
        ) : (
          <SkipForward size={16} />
        )}
        Passer sans contrat
      </Button>
    </div>
  );
}
